// src/pages/AdminDeposits.js
import React, { useEffect, useState, useContext } from "react";
import axios from "axios";
import { Box, Typography } from "@mui/material";
import SavingsIcon from "@mui/icons-material/Savings";
import { AuthContext } from "../context/AuthContext";
import toast from "react-hot-toast";

const AdminDeposits = () => {
  const { logout } = useContext(AuthContext);

  const [deposits, setDeposits] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filterStatus, setFilterStatus] = useState("all"); // all | active | settled
  const [rowActionLoading, setRowActionLoading] = useState(null); // deposit id

  const token = localStorage.getItem("token");
  const refreshToken = localStorage.getItem("refreshToken");
  const API = process.env.REACT_APP_API_URL;

  const baseConfig = {
    headers: { Authorization: `Bearer ${token}` },
  };

  const handleLogout = () => {
    logout();
    window.location.href = "/";
  };

  const refreshAccessToken = async () => {
    try {
      const res = await axios.post(`${API}token/refresh/`, {
        refresh: refreshToken,
      });
      const { access, refresh } = res.data;
      localStorage.setItem("token", access);
      localStorage.setItem("refreshToken", refresh);
      return access;
    } catch (err) {
      console.error("Token refresh failed:", err);
      handleLogout();
      throw err;
    }
  };

  const makeAuthenticatedRequest = async (apiCall) => {
    try {
      return await apiCall();
    } catch (error) {
      if (error.response?.status === 401) {
        try {
          const newToken = await refreshAccessToken();
          const newConfig = {
            headers: { Authorization: `Bearer ${newToken}` },
          };
          return await apiCall(newConfig);
        } catch (refreshError) {
          handleLogout();
          throw refreshError;
        }
      }
      throw error;
    }
  };

  const fetchDeposits = async () => {
    setLoading(true);
    const loadingToast = toast.loading("Loading deposit records...");
    try {
      const res = await makeAuthenticatedRequest(
        async (newConfig = baseConfig) => {
          return axios.get(`${API}library/deposits/`, newConfig);
        }
      );
      setDeposits(res.data || []);
      toast.dismiss(loadingToast);
      setLoading(false);
    } catch (err) {
      console.error(err);
      toast.dismiss(loadingToast);
      toast.error("❌ Failed to load deposit data");
      setLoading(false);
    }
  };

  // Mark settled / not-settled handler
  const markDepositStatus = async (depositId, toSettled) => {
    const label = toSettled ? "settled" : "not settled";
    const loadingToast = toast.loading(
      `Updating deposit as ${label}...`
    );
    setRowActionLoading(depositId);

    const primaryPath = toSettled ? "mark-settled" : "mark-unsettled";
    const fallbackPath = toSettled ? "mark_settled" : "mark_unsettled";

    try {
      await makeAuthenticatedRequest(async (newConfig = baseConfig) => {
        try {
          // Try hyphen version first
          return await axios.post(
            `${API}library/deposits/${depositId}/${primaryPath}/`,
            {},
            newConfig
          );
        } catch (err) {
          // If 404, try underscore version
          if (err.response && err.response.status === 404) {
            return await axios.post(
              `${API}library/deposits/${depositId}/${fallbackPath}/`,
              {},
              newConfig
            );
          }
          throw err;
        }
      });

      toast.dismiss(loadingToast);
      toast.success(
        toSettled
          ? "✅ Deposit marked as settled."
          : "✅ Deposit marked as not settled."
      );
      await fetchDeposits();
    } catch (err) {
      console.error(err);
      toast.dismiss(loadingToast);
      toast.error("❌ Failed to update deposit status");
    } finally {
      setRowActionLoading(null);
    }
  };

  useEffect(() => {
    fetchDeposits();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Filtered list based on status
  const filteredDeposits = deposits.filter((d) => {
    if (filterStatus === "all") return true;
    if (filterStatus === "active") return !d.is_settled;
    if (filterStatus === "settled") return d.is_settled;
    return true;
  });

  const activeCount = deposits.filter((d) => !d.is_settled).length;
  const settledCount = deposits.filter((d) => d.is_settled).length;

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 p-4 md:p-6">
      <div className="max-w-full mx-auto space-y-6">
        {/* Header Card */}
        <div className="bg-white rounded-xl shadow-lg overflow-hidden">
          <div className="bg-gradient-to-r from-blue-600 to-indigo-600 p-6 text-white">
            <div className="flex items-center gap-3 mb-2">
              <div className="p-2 bg-white/20 rounded-lg">
                <SavingsIcon className="text-white text-2xl" />
              </div>
              <div>
                <h2 className="text-xl font-bold">Library Deposits</h2>
                <p className="text-blue-100 text-sm">
                  Track active and settled security deposits of all students
                </p>
              </div>
            </div>
          </div>

          <div className="p-4 md:p-6">
            {/* Filter Bar */}
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 mb-4">
              <div className="flex flex-wrap gap-2">
                <button
                  onClick={() => setFilterStatus("all")}
                  className={`px-3 py-1.5 rounded-full text-xs md:text-sm font-semibold border transition ${
                    filterStatus === "all"
                      ? "bg-blue-600 text-white border-blue-600 shadow-sm"
                      : "bg-gray-50 text-gray-700 border-gray-200 hover:bg-gray-100"
                  }`}
                >
                  All ({deposits.length})
                </button>
                <button
                  onClick={() => setFilterStatus("active")}
                  className={`px-3 py-1.5 rounded-full text-xs md:text-sm font-semibold border transition ${
                    filterStatus === "active"
                      ? "bg-yellow-500 text-white border-yellow-500 shadow-sm"
                      : "bg-gray-50 text-gray-700 border-gray-200 hover:bg-gray-100"
                  }`}
                >
                  Active / Not Settled ({activeCount})
                </button>
                <button
                  onClick={() => setFilterStatus("settled")}
                  className={`px-3 py-1.5 rounded-full text-xs md:text-sm font-semibold border transition ${
                    filterStatus === "settled"
                      ? "bg-green-600 text-white border-green-600 shadow-sm"
                      : "bg-gray-50 text-gray-700 border-gray-200 hover:bg-gray-100"
                  }`}
                >
                  Settled ({settledCount})
                </button>
              </div>

              <div className="text-xs md:text-sm text-gray-500">
                <span className="font-semibold text-gray-700">
                  Quick Summary:
                </span>{" "}
                {activeCount} active, {settledCount} settled deposits
              </div>
            </div>

            {loading ? (
              <Box className="flex items-center justify-center h-40">
                <Box className="text-center">
                  <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600 mx-auto mb-3"></div>
                  <Typography className="text-gray-600 text-sm">
                    Loading deposit records...
                  </Typography>
                </Box>
              </Box>
            ) : filteredDeposits.length === 0 ? (
              <Box className="bg-blue-50 border border-blue-100 rounded-lg p-4 text-center">
                <Typography className="text-gray-700 text-sm">
                  No deposit records found for selected filter.
                </Typography>
              </Box>
            ) : (
              <div className="overflow-x-auto">
                <table className="min-w-full border border-gray-200 bg-white rounded-lg overflow-hidden">
                  <thead className="bg-gray-100">
                    <tr>
                      <th className="px-3 py-2 text-left text-xs md:text-sm font-semibold text-gray-700 border-b">
                        Student
                      </th>
                      <th className="px-3 py-2 text-left text-xs md:text-sm font-semibold text-gray-700 border-b">
                        Booking ID
                      </th>
                      <th className="px-3 py-2 text-left text-xs md:text-sm font-semibold text-gray-700 border-b">
                        Amount (₹)
                      </th>
                      <th className="px-3 py-2 text-left text-xs md:text-sm font-semibold text-gray-700 border-b">
                        Status
                      </th>
                      <th className="px-3 py-2 text-left text-xs md:text-sm font-semibold text-gray-700 border-b">
                        Paid On
                      </th>
                      <th className="px-3 py-2 text-left text-xs md:text-sm font-semibold text-gray-700 border-b">
                        Settled On
                      </th>
                      <th className="px-3 py-2 text-left text-xs md:text-sm font-semibold text-gray-700 border-b">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredDeposits.map((deposit) => (
                      <tr
                        key={deposit.id}
                        className="hover:bg-gray-50 transition-colors text-xs md:text-sm"
                      >
                        <td className="px-3 py-2 border-b text-gray-800">
                          {deposit.student?.first_name}{" "}
                          {deposit.student?.last_name}{" "}
                          <span className="text-gray-400 text-[11px] block md:inline">
                            ({deposit.student?.username})
                          </span>
                        </td>
                        <td className="px-3 py-2 border-b text-gray-700">
                          {deposit.booking_id || "-"}
                        </td>
                        <td className="px-3 py-2 border-b font-semibold text-gray-800">
                          {deposit.amount}
                        </td>
                        <td className="px-3 py-2 border-b">
                          <span
                            className={`inline-flex px-2 py-1 rounded-full text-[11px] font-semibold ${
                              deposit.is_settled
                                ? "bg-green-100 text-green-700"
                                : "bg-yellow-100 text-yellow-700"
                            }`}
                          >
                            {deposit.is_settled ? "Settled" : "Not Settled"}
                          </span>
                        </td>
                        <td className="px-3 py-2 border-b text-gray-700">
                          {deposit.paid_on
                            ? new Date(deposit.paid_on).toLocaleDateString()
                            : "-"}
                        </td>
                        <td className="px-3 py-2 border-b text-gray-700">
                          {deposit.settled_on
                            ? new Date(
                                deposit.settled_on
                              ).toLocaleDateString()
                            : "-"}
                        </td>
                        <td className="px-3 py-2 border-b">
                          <div className="flex flex-col sm:flex-row gap-2">
                            {deposit.is_settled ? (
                              <button
                                disabled={rowActionLoading === deposit.id}
                                onClick={() =>
                                  markDepositStatus(deposit.id, false)
                                }
                                className={`px-3 py-1 rounded-full text-[11px] font-semibold border transition ${
                                  rowActionLoading === deposit.id
                                    ? "bg-gray-200 text-gray-500 cursor-not-allowed"
                                    : "bg-white text-gray-700 border-gray-300 hover:bg-gray-100"
                                }`}
                              >
                                {rowActionLoading === deposit.id
                                  ? "Updating..."
                                  : "Mark Not Settled"}
                              </button>
                            ) : (
                              <button
                                disabled={rowActionLoading === deposit.id}
                                onClick={() =>
                                  markDepositStatus(deposit.id, true)
                                }
                                className={`px-3 py-1 rounded-full text-[11px] font-semibold border transition ${
                                  rowActionLoading === deposit.id
                                    ? "bg-blue-300 text-white cursor-not-allowed"
                                    : "bg-blue-600 text-white border-blue-600 hover:bg-blue-700"
                                }`}
                              >
                                {rowActionLoading === deposit.id
                                  ? "Updating..."
                                  : "Mark Settled"}
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>

        {/* Info Card */}
        <div className="bg-white rounded-xl shadow-lg p-4 md:p-6">
          <div className="flex items-start gap-4">
            <div className="p-3 bg-blue-100 rounded-lg">
              <span className="text-blue-600 text-2xl">💡</span>
            </div>
            <div>
              <h3 className="text-lg font-semibold text-gray-800 mb-2">
                Deposit Tracking Tips
              </h3>
              <div className="space-y-2 text-sm text-gray-600">
                <p>• "Not Settled" means deposit is still held by the center.</p>
                <p>
                  • "Settled" means deposit is refunded or adjusted at checkout.
                </p>
                <p>
                  • Use filters above to quickly switch between Active and
                  Settled deposits.
                </p>
                <p>
                  • Use this report for financial audit and refund management.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminDeposits;
